import React from "react";

const CHANNEL_COLORS = {
    meta: "#FF5A5F",
    google: "#A3E635",
    amazon: "#0f172a",
    youtube: "#64748b",
    pinterest: "#E60023",
    snap: "#FFFC00",
    tiktok: "#25F4EE",
};
const CHANNEL_LABELS = {
    meta: "Meta",
    google: "Google",
    amazon: "Amazon",
    youtube: "YouTube",
    pinterest: "Pinterest",
    snap: "Snap",
    tiktok: "TikTok",
};

// Normalize channel keys so the backend can return "Google", "google_ads",
// "Meta Ads", etc. and we still color-code correctly.
function normalizeChannel(ch) {
    if (!ch) return "";
    const lc = String(ch).toLowerCase();
    if (lc.includes("meta") || lc.includes("facebook") || lc.includes("instagram"))
        return "meta";
    if (lc.includes("google") || lc.includes("gads")) return "google";
    if (lc.includes("amazon")) return "amazon";
    if (lc.includes("youtube") || lc.includes("yt")) return "youtube";
    if (lc.includes("pinterest")) return "pinterest";
    if (lc.includes("snap")) return "snap";
    if (lc.includes("tiktok") || lc.includes("tt")) return "tiktok";
    return lc;
}
function channelColor(ch) {
    return CHANNEL_COLORS[normalizeChannel(ch)] || "#64748b";
}
function channelLabel(ch) {
    return CHANNEL_LABELS[normalizeChannel(ch)] || ch;
}

/** Aggregates per-video data into a creator × channel matrix.
 *  For each video we split its revenue evenly across the channels it ran on.
 *  Commission uses the total `creator_paid` for that creator (ground truth).
 */
function buildMatrix(byVideo, byCreator) {
    const perCreatorChannel = {}; // creator_id → { channel → revenue }
    const perCreatorVideos = {}; // creator_id → count

    for (const v of byVideo || []) {
        const cid = v.creator_id;
        const channels = (v.channels || []).filter(Boolean);
        if (channels.length === 0) continue;
        perCreatorChannel[cid] = perCreatorChannel[cid] || {};
        perCreatorVideos[cid] = (perCreatorVideos[cid] || 0) + 1;
        const perCh = (v.revenue || 0) / channels.length;
        for (const ch of channels) {
            perCreatorChannel[cid][ch] =
                (perCreatorChannel[cid][ch] || 0) + perCh;
        }
    }

    const creatorMeta = {};
    for (const c of byCreator || []) {
        creatorMeta[c.creator_id] = {
            creator_name: c.creator_name,
            revenue: c.revenue || 0,
            commission: c.creator_paid || 0,
            videos: c.videos || 0,
        };
    }

    // Build final list, sorted by revenue desc
    const rows = Object.keys(perCreatorChannel).map((cid) => ({
        creator_id: cid,
        creator_name:
            creatorMeta[cid]?.creator_name ||
            byVideo.find((v) => v.creator_id === cid)?.creator_name ||
            "Creator",
        revenue: creatorMeta[cid]?.revenue || 0,
        commission: creatorMeta[cid]?.commission || 0,
        channels: perCreatorChannel[cid],
        videos: creatorMeta[cid]?.videos || perCreatorVideos[cid] || 0,
    }));
    rows.sort((a, b) => b.revenue - a.revenue);
    return rows;
}

export default function CreatorChannelMatrix({ data }) {
    const rows = React.useMemo(
        () => buildMatrix(data.by_video || [], data.by_creator || []),
        [data],
    );

    if (rows.length === 0) {
        return (
            <div
                className="card-ecom p-10 text-center text-sm text-slate-500"
                data-testid="matrix-empty"
            >
                Run a few ads and Castline will start attributing revenue back per creator and per channel.
            </div>
        );
    }

    // Active channels across all rows for legend + bar scaling
    const activeChannels = Array.from(
        new Set(rows.flatMap((r) => Object.keys(r.channels))),
    );
    const maxChannelRevenue = Math.max(
        1,
        ...rows.flatMap((r) => Object.values(r.channels)),
    );

    return (
        <div
            className="card-ecom p-6 md:p-8"
            data-testid="creator-channel-matrix"
        >
            <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
                <div className="overline text-coral">PER-CREATOR · PER-CHANNEL ATTRIBUTION</div>
                <div className="flex items-center gap-3 flex-wrap text-[11px] text-slate-500">
                    {activeChannels.map((ch) => (
                        <div key={ch} className="inline-flex items-center gap-1.5">
                            <span
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ background: channelColor(ch) }}
                            />
                            {channelLabel(ch)}
                        </div>
                    ))}
                </div>
            </div>
            <div className="font-display text-lg md:text-xl font-semibold mb-5 text-slate-900">
                See which creator drives revenue — on which channel.
            </div>

            <div className="space-y-5">
                {rows.map((r) => (
                    <div key={r.creator_id} data-testid={`matrix-row-${r.creator_id}`}>
                        <div className="flex items-baseline justify-between mb-1.5 flex-wrap gap-2">
                            <div className="font-display text-sm font-semibold text-slate-900">
                                {r.creator_name}
                                <span className="ml-2 text-[11px] text-slate-400 font-normal">
                                    {r.videos} videos
                                </span>
                            </div>
                            <div className="flex items-baseline gap-5 text-xs text-slate-500">
                                <span>
                                    revenue{" "}
                                    <span className="font-display font-semibold text-slate-900">
                                        ${Math.round(r.revenue).toLocaleString()}
                                    </span>
                                </span>
                                <span>
                                    commission{" "}
                                    <span
                                        className="font-display font-semibold"
                                        style={{ color: "#FF5A5F" }}
                                    >
                                        ${Math.round(r.commission).toLocaleString()}
                                    </span>
                                </span>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            {Object.entries(r.channels)
                                .sort((a, b) => b[1] - a[1])
                                .map(([ch, val]) => {
                                    const pct = (val / maxChannelRevenue) * 100;
                                    return (
                                        <div key={ch} className="flex items-center gap-3">
                                            <div className="w-16 text-[11px] text-slate-500">
                                                {channelLabel(ch)}
                                            </div>
                                            <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                                                <div
                                                    className="h-full rounded-full"
                                                    style={{
                                                        width: `${pct}%`,
                                                        background: channelColor(ch),
                                                    }}
                                                />
                                            </div>
                                            <div className="w-20 text-right text-[11px] font-display font-semibold">
                                                ${Math.round(val).toLocaleString()}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-6 pt-5 border-t hairline text-xs text-slate-500 leading-relaxed">
                One dashboard — not a merged line. Every dollar of attributed revenue is traced back to the creator and the channel that drove it. Commission reflects what's been paid (or is owed) to each creator under their current payout terms.
            </div>
        </div>
    );
}
